import React from 'react';
import { Alert, Box, Grid, GridProps, Typography } from "@mui/material";
import { formatCurrencyValue } from "../../util/PaymentUtils";

export interface ReadyForPayoutSummary {
  totalChargeAmount?: number;
  totalRefundAmount?: number;
  netPayoutAmount?: number;
  chargeCount?: number;
  refundCount?: number;
}

interface PayoutSummaryProps {
  summary?: ReadyForPayoutSummary;
  title?: string;
  gridItemProps?: GridProps;
}

const money = (cents = 0): string =>
  cents < 0 ? `-${formatCurrencyValue(-cents, true)}` : `${formatCurrencyValue(cents, true)}`;

const pluralize = (count: number, noun: string): string => `${count} ${noun}${count === 1 ? '' : 's'}`;

interface LedgerRowProps {
  label: string;
  detail?: string;
  amount: string;
  negative?: boolean;
  total?: boolean;
}

const LedgerRow = ({ label, detail, amount, negative = false, total = false }: LedgerRowProps) => (
  <Box
    display={'flex'}
    justifyContent={'space-between'}
    alignItems={'baseline'}
    gap={2}
    py={total ? 1.25 : 0.75}
    sx={total ? { borderTop: 1, borderColor: 'text.primary', mt: 0.5 } : undefined}
  >
    <Typography variant={'body1'} fontWeight={total ? 600 : 400}>
      {label}
      {detail && (
        <Typography component={'span'} variant={'body2'} color={'text.secondary'} sx={{ ml: 0.75 }}>
          {detail}
        </Typography>
      )}
    </Typography>
    <Typography
      variant={total ? 'h6' : 'body1'}
      fontWeight={total ? 700 : 500}
      color={negative ? 'error.main' : 'text.primary'}
      sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
    >
      {amount}
    </Typography>
  </Box>
);

/**
 * Ledger-style breakdown of the ready-for-payout number: charges, pending refund deductions, and the net the
 * supplier will actually receive, with a rule above the total like an invoice footer. Amounts are integer cents.
 * Two columns with no fixed widths, so it takes the full width on a phone.
 *
 * @param summary Server-provided summary (charges, refunds, net, counts)
 * @param title Optional suffix, typically the selected location name
 * @param gridItemProps Optional MUI Grid props for the container
 */
const PayoutSummary = ({ summary = {}, title = '', gridItemProps }: PayoutSummaryProps) => {
  const {
    totalChargeAmount = 0,
    totalRefundAmount = 0,
    netPayoutAmount = totalChargeAmount - totalRefundAmount,
    chargeCount = 0,
    refundCount = 0,
  } = summary;
  const noPayout = refundCount > 0 && netPayoutAmount <= 0;

  return (
    <Grid item container direction={'column'} {...gridItemProps}>
      <Box maxWidth={440} width={'100%'}>
        <LedgerRow label={'Charges ready'} detail={pluralize(chargeCount, 'invoice')} amount={money(totalChargeAmount)} />
        <LedgerRow
          label={'Refund deductions'}
          detail={pluralize(refundCount, 'refund')}
          amount={money(-totalRefundAmount)}
          negative={totalRefundAmount > 0}
        />
        <LedgerRow
          label={title ? `Net payout ${title}` : 'Net payout'}
          amount={money(netPayoutAmount)}
          negative={netPayoutAmount < 0}
          total
        />
      </Box>
      {noPayout && (
        <Alert severity={'warning'} sx={{ mt: 1.5, maxWidth: 640 }}>
          No payout will be issued. Pending refunds equal or exceed the charges ready for payout; the balance carries to the next payout run.
        </Alert>
      )}
    </Grid>
  );
};

export default PayoutSummary;
